import { useState, useCallback } from "react";
import {
  RefreshCcw,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Clock,
  Play,
  CheckCircle2,
  XCircle,
  Timer,
  Inbox,
  ChevronDown,
  FastForward,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { useLocale } from "../../../context/LanguageContext";
import {
  useQueueOverview,
  useQueueJobs,
  useQueueDlqJobs,
  useRetryQueueJob,
  useRemoveQueueJob,
  useRetryAllFailedJobs,
  useCleanQueueJobs,
  usePromoteQueueJob,
} from "../hooks/useQueueMonitoring";
import type { QueueStats, QueueJobStatus, QueueJob } from "../types";

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: QueueJobStatus; icon: React.ElementType }[] = [
  { value: "WAITING", icon: Clock },
  { value: "ACTIVE", icon: Play },
  { value: "COMPLETED", icon: CheckCircle2 },
  { value: "FAILED", icon: XCircle },
  { value: "DELAYED", icon: Timer },
];

// Maps a BullMQ job state (lower-cased) to its i18n label key. Resolved with
// `t()` at each render site — never rendered raw. Falls back to the raw value.
const STATE_LABEL_KEYS: Record<string, string> = {
  waiting: "adminUI.queues.state.waiting",
  active: "adminUI.queues.state.active",
  completed: "adminUI.queues.state.completed",
  failed: "adminUI.queues.state.failed",
  delayed: "adminUI.queues.state.delayed",
  paused: "adminUI.queues.state.paused",
};

// ─── Main Dashboard ──────────────────────────────────────────────────────────

export function QueueDashboard() {
  const { t } = useLocale();
  const { data, loading, error, refetch } = useQueueOverview();
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<QueueJobStatus>("FAILED");
  const [showDlq, setShowDlq] = useState(false);

  if (error) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-6 dark:border-red-800 dark:bg-red-950/30">
        <p className="text-sm text-red-700 dark:text-red-300">
          {t("adminUI.queues.loadError", { message: error.message })}
        </p>
      </div>
    );
  }

  const overview = data?.queueOverview;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("adminUI.queues.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("adminUI.queues.subtitle")}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <RefreshCcw className="h-4 w-4" />
          {t("common.refresh")}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          label={t("adminUI.queues.totalWaiting")}
          value={overview?.totalWaiting ?? 0}
          icon={<Clock className="h-5 w-5" />}
          variant="warning"
          loading={loading}
        />
        <SummaryCard
          label={t("adminUI.queues.totalActive")}
          value={overview?.totalActive ?? 0}
          icon={<Play className="h-5 w-5" />}
          variant="info"
          loading={loading}
        />
        <SummaryCard
          label={t("adminUI.queues.totalFailed")}
          value={overview?.totalFailed ?? 0}
          icon={<AlertTriangle className="h-5 w-5" />}
          variant="danger"
          loading={loading}
        />
      </div>

      {/* Queue Cards Grid */}
      {loading && !overview ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <QueueCardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {overview?.queues.map((queue) => {
            const dlq = overview.dlqs?.find(
              (d) => d.name === `${queue.name}-dlq`,
            );
            return (
              <QueueCard
                key={queue.name}
                queue={queue}
                dlqFailed={dlq ? dlq.waiting + dlq.failed : 0}
                isSelected={selectedQueue === queue.name}
                onSelect={() => {
                  setSelectedQueue(
                    selectedQueue === queue.name ? null : queue.name,
                  );
                  setShowDlq(false);
                }}
                onShowDlq={() => {
                  setSelectedQueue(queue.name);
                  setShowDlq(true);
                }}
              />
            );
          })}
        </div>
      )}

      {/* Job Detail Panel */}
      {selectedQueue && (
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-foreground">
                {selectedQueue}
              </h2>
              <div className="flex gap-1">
                <button
                  onClick={() => setShowDlq(false)}
                  className={cn(
                    "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                    !showDlq
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t("adminUI.queues.tabJobs")}
                </button>
                <button
                  onClick={() => setShowDlq(true)}
                  className={cn(
                    "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                    showDlq
                      ? "bg-red-600 text-white"
                      : "bg-muted text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t("adminUI.queues.tabDlq")}
                </button>
              </div>
            </div>
            <QueueActions queueName={selectedQueue} showDlq={showDlq} />
          </div>

          {showDlq ? (
            <DlqJobList queueName={selectedQueue} />
          ) : (
            <div>
              {/* Status Filter */}
              <div className="flex gap-2 border-b border-border px-6 py-3">
                {STATUS_OPTIONS.map(({ value, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => setSelectedStatus(value)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      selectedStatus === value
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Icon className="h-3 w-3" />
                    {t(STATE_LABEL_KEYS[value.toLowerCase()] ?? value)}
                  </button>
                ))}
              </div>
              <JobList queueName={selectedQueue} status={selectedStatus} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Summary Card ────────────────────────────────────────────────────────────

interface SummaryCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  variant: "warning" | "info" | "danger";
  loading: boolean;
}

function SummaryCard({ label, value, icon, variant, loading }: SummaryCardProps) {
  const variantStyles = {
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",
    danger: "text-red-600 dark:text-red-400",
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="h-4 w-24 animate-pulse rounded bg-muted" />
        <div className="mt-3 h-8 w-16 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <div className={variantStyles[variant]}>{icon}</div>
      </div>
      <p className={cn("mt-2 text-3xl font-bold", variantStyles[variant])}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}

// ─── Queue Card ──────────────────────────────────────────────────────────────

interface QueueCardProps {
  queue: QueueStats;
  dlqFailed: number;
  isSelected: boolean;
  onSelect: () => void;
  onShowDlq: () => void;
}

function QueueCard({ queue, dlqFailed, isSelected, onSelect, onShowDlq }: QueueCardProps) {
  const { t } = useLocale();
  return (
    <div
      className={cn(
        "cursor-pointer rounded-lg border bg-card p-5 transition-colors hover:border-primary/50",
        isSelected ? "border-primary ring-1 ring-primary/20" : "border-border",
      )}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{queue.name}</h3>
        {queue.isPaused && (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            {t("adminUI.queues.state.paused")}
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <StatBadge label={t("adminUI.queues.state.waiting")} value={queue.waiting} variant="amber" />
        <StatBadge label={t("adminUI.queues.state.active")} value={queue.active} variant="blue" />
        <StatBadge label={t("adminUI.queues.state.failed")} value={queue.failed} variant="red" />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center">
        <StatBadge label={t("adminUI.queues.state.delayed")} value={queue.delayed} variant="purple" />
        <StatBadge label={t("adminUI.queues.stat.done")} value={queue.completed} variant="green" />
        <StatBadge label={t("adminUI.queues.state.paused")} value={queue.paused} variant="gray" />
      </div>
      {dlqFailed > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onShowDlq();
          }}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-950/50 transition-colors"
        >
          <AlertTriangle className="h-3 w-3" />
          {t(
            dlqFailed === 1
              ? "adminUI.queues.dlqJobCount_one"
              : "adminUI.queues.dlqJobCount_other",
            { count: dlqFailed },
          )}
        </button>
      )}
    </div>
  );
}

function StatBadge({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "amber" | "blue" | "red" | "purple" | "green" | "gray";
}) {
  const colors = {
    amber: "text-amber-600 dark:text-amber-400",
    blue: "text-blue-600 dark:text-blue-400",
    red: "text-red-600 dark:text-red-400",
    purple: "text-purple-600 dark:text-purple-400",
    green: "text-emerald-600 dark:text-emerald-400",
    gray: "text-muted-foreground",
  };

  return (
    <div>
      <p className={cn("text-lg font-bold", colors[variant])}>
        {value.toLocaleString()}
      </p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function QueueCardSkeleton() {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      <div className="mt-4 grid grid-cols-3 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <div className="h-5 w-8 animate-pulse rounded bg-muted" />
            <div className="h-2 w-10 animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Queue Actions ───────────────────────────────────────────────────────────

function QueueActions({
  queueName,
  showDlq,
}: {
  queueName: string;
  showDlq: boolean;
}) {
  const { t } = useLocale();
  const [retryAll, { loading: retryingAll }] = useRetryAllFailedJobs();
  const [clean, { loading: cleaning }] = useCleanQueueJobs();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleRetryAll = useCallback(async () => {
    const result = await retryAll({ variables: { queueName, isDlq: showDlq } });
    const count = result.data?.retryAllFailedJobs ?? 0;
    const key = showDlq
      ? count === 1
        ? "adminUI.queues.retriedDlqAlert_one"
        : "adminUI.queues.retriedDlqAlert_other"
      : count === 1
        ? "adminUI.queues.retriedAlert_one"
        : "adminUI.queues.retriedAlert_other";
    alert(t(key, { count }));
  }, [queueName, showDlq, retryAll, t]);

  const handleClean = useCallback(
    async (status: string) => {
      const count = (
        await clean({
          variables: { queueName, status, gracePeriodHours: 24 },
        })
      ).data?.cleanQueueJobs ?? 0;
      const statusLabel = t(STATE_LABEL_KEYS[status] ?? status);
      alert(
        t(
          count === 1
            ? "adminUI.queues.cleanedAlert_one"
            : "adminUI.queues.cleanedAlert_other",
          { count, status: statusLabel },
        ),
      );
      setDropdownOpen(false);
    },
    [queueName, clean, t],
  );

  return (
    <div className="flex items-center gap-2">
      {(showDlq || true) && (
        <button
          onClick={handleRetryAll}
          disabled={retryingAll}
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-200 disabled:opacity-50 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 transition-colors"
        >
          <RotateCcw className={cn("h-3 w-3", retryingAll && "animate-spin")} />
          {t("adminUI.queues.retryAllFailed")}
        </button>
      )}
      <div className="relative">
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="inline-flex items-center gap-1.5 rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <Trash2 className="h-3 w-3" />
          {t("adminUI.queues.clean")}
          <ChevronDown className="h-3 w-3" />
        </button>
        {dropdownOpen && (
          <div className="absolute right-0 top-full z-10 mt-1 rounded-md border border-border bg-popover py-1 shadow-lg">
            <button
              onClick={() => handleClean("completed")}
              disabled={cleaning}
              className="block w-full px-4 py-2 text-left text-xs hover:bg-muted transition-colors"
            >
              {t("adminUI.queues.cleanCompleted")}
            </button>
            <button
              onClick={() => handleClean("failed")}
              disabled={cleaning}
              className="block w-full px-4 py-2 text-left text-xs text-red-600 hover:bg-muted dark:text-red-400 transition-colors"
            >
              {t("adminUI.queues.cleanFailed")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Job List ────────────────────────────────────────────────────────────────

function JobList({
  queueName,
  status,
}: {
  queueName: string;
  status: QueueJobStatus;
}) {
  const { t } = useLocale();
  const { data, loading } = useQueueJobs({ queueName, status, limit: 20, offset: 0 });

  if (loading) {
    return <JobListSkeleton />;
  }

  const jobs = data?.queueJobs.jobs ?? [];

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Inbox className="mb-2 h-8 w-8" />
        <p className="text-sm">
          {t("adminUI.queues.noJobs", {
            status: t(STATE_LABEL_KEYS[status.toLowerCase()] ?? status),
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.id")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.jobName")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.attempts")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.created")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("common.error")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} queueName={queueName} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DlqJobList({ queueName }: { queueName: string }) {
  const { t } = useLocale();
  const { data, loading } = useQueueDlqJobs(queueName);

  if (loading) {
    return <JobListSkeleton />;
  }

  const jobs = data?.queueDlqJobs.jobs ?? [];

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <CheckCircle2 className="mb-2 h-8 w-8" />
        <p className="text-sm">{t("adminUI.queues.noDlqJobs")}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.id")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.jobName")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.attempts")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.created")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("common.error")}</th>
            <th className="px-6 py-3 font-medium text-muted-foreground">{t("adminUI.queues.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} queueName={queueName} isDlq />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Job Row ─────────────────────────────────────────────────────────────────

function JobRow({ job, queueName, isDlq = false }: { job: QueueJob; queueName: string; isDlq?: boolean }) {
  const { t } = useLocale();
  const [retry, { loading: retrying }] = useRetryQueueJob();
  const [remove, { loading: removing }] = useRemoveQueueJob();
  const [promote, { loading: promoting }] = usePromoteQueueJob();

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
      <td className="px-6 py-3 font-mono text-xs text-muted-foreground">
        {job.id.length > 12 ? `${job.id.slice(0, 12)}…` : job.id}
      </td>
      <td className="px-6 py-3 font-medium text-foreground">{job.name}</td>
      <td className="px-6 py-3 text-muted-foreground">
        {job.attemptsMade}/{job.maxAttempts}
      </td>
      <td className="px-6 py-3 text-xs text-muted-foreground">
        {formatDate(job.timestamp)}
      </td>
      <td className="max-w-xs truncate px-6 py-3 text-xs text-red-600 dark:text-red-400">
        {job.failedReason ?? "—"}
      </td>
      <td className="px-6 py-3">
        <div className="flex items-center gap-1">
          {job.status === "delayed" && !isDlq && (
            <button
              onClick={() =>
                promote({ variables: { queueName, jobId: job.id } })
              }
              disabled={promoting}
              title={t("adminUI.queues.runNow")}
              className="rounded p-1 text-muted-foreground hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50 dark:hover:bg-emerald-950/30 dark:hover:text-emerald-400 transition-colors"
            >
              <FastForward className={cn("h-3.5 w-3.5", promoting && "animate-pulse")} />
            </button>
          )}
          {(job.status === "failed" || job.failedReason || isDlq) && (
            <button
              onClick={() =>
                retry({ variables: { queueName, jobId: job.id, isDlq } })
              }
              disabled={retrying}
              title={isDlq ? t("adminUI.queues.retryDlq") : t("adminUI.queues.retry")}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 transition-colors"
            >
              <RotateCcw className={cn("h-3.5 w-3.5", retrying && "animate-spin")} />
            </button>
          )}
          <button
            onClick={() =>
              remove({ variables: { queueName, jobId: job.id, isDlq } })
            }
            disabled={removing}
            title={t("adminUI.queues.remove")}
            className="rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/30 dark:hover:text-red-400 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function JobListSkeleton() {
  return (
    <div className="space-y-3 p-6">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
          <div className="h-4 w-32 animate-pulse rounded bg-muted" />
          <div className="h-4 w-12 animate-pulse rounded bg-muted" />
          <div className="h-4 w-24 animate-pulse rounded bg-muted" />
          <div className="h-4 flex-1 animate-pulse rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}
